interface ApiErrorBody {
  error?: { code?: string; message?: string; requestId?: string; details?: Record<string, unknown> };
}

export class ApiFailure extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId?: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
  }
}

function browserCookie(name: string): string | undefined {
  const prefix = `${encodeURIComponent(name)}=`;
  const entry = document.cookie.split(";").map((value) => value.trim()).find((value) => value.startsWith(prefix));
  return entry ? decodeURIComponent(entry.slice(prefix.length)) : undefined;
}

function browserDeviceId(): string {
  const key = "lmx.registration-device.v1";
  const created = crypto.randomUUID();
  try {
    const stored = localStorage.getItem(key)?.trim() ?? "";
    if (/^[A-Za-z0-9._:-]{16,128}$/u.test(stored)) return stored;
    localStorage.setItem(key, created);
  } catch {
    // Storage-restricted browsers use a fresh per-page value.
  }
  return created;
}

let fallbackDeviceId: string | undefined;

export async function api<T>(path: string, init: RequestInit = {}): Promise<{
  data: T;
  requestId?: string;
  meta?: unknown;
  uploadCredential?: { token: string };
  statusCredential?: { token: string };
}> {
  const method = (init.method ?? "GET").toUpperCase();
  const unsafe = !["GET", "HEAD", "OPTIONS"].includes(method);
  const defaultHeaders: Record<string, string> = {};
  if (path.startsWith("/v2/")) {
    fallbackDeviceId ??= browserDeviceId();
    defaultHeaders["x-lmx-device-id"] = fallbackDeviceId;
    const csrf = unsafe ? browserCookie("lmx_csrf") : undefined;
    if (csrf) defaultHeaders["x-csrf-token"] = csrf;
  }
  const response = await fetch(path, {
    credentials: "include",
    ...init,
    headers: {
      ...(init.body && !(init.body instanceof Blob) && !(init.body instanceof ArrayBuffer)
        ? { "content-type": "application/json" }
        : {}),
      ...defaultHeaders,
      ...init.headers,
    },
  });
  if (response.status === 204) return { data: undefined as T };
  const body = await response.json() as ApiErrorBody & {
    data?: T;
    requestId?: string;
    meta?: unknown;
    uploadCredential?: { token: string };
    statusCredential?: { token: string };
  };
  if (!response.ok) {
    throw new ApiFailure(
      response.status,
      body.error?.code ?? "REQUEST_FAILED",
      body.error?.message ?? "请求失败，请稍后重试。",
      body.error?.requestId,
      body.error?.details,
    );
  }
  return {
    data: body.data as T,
    requestId: body.requestId,
    meta: body.meta,
    uploadCredential: body.uploadCredential,
    statusCredential: body.statusCredential,
  };
}
